import { useEffect, useState } from "react";
import { Bell, AlertTriangle, IndianRupee, Package, X } from "lucide-react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useNavigate } from "react-router-dom";

const ICONS = { low_stock: Package, overdue_invoice: AlertTriangle, unallocated_payment: IndianRupee };

export function NotificationsBell() {
  const { activeId } = useCompany();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const load = () => {
    if (!activeId) return;
    api.get("/notifications").then((r) => setItems(r.data.items || [])).catch(() => {});
  };
  useEffect(load, [activeId, open]); // eslint-disable-line

  const dismiss = async (e, id) => {
    e.stopPropagation();
    await api.post("/notifications/dismiss", { alert_id: id }).catch(() => {});
    setItems((p) => p.filter((a) => a.id !== id));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid="notifications-bell" variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-4.5 w-4.5 h-5 w-5" />
          {items.length > 0 && (
            <span data-testid="notifications-count"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
              {items.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
        <div className="border-b border-slate-100 dark:border-slate-800 px-4 py-3">
          <p className="font-semibold font-display text-sm">Notifications</p>
          <p className="text-xs text-slate-500">{items.length} alerts need attention</p>
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 && <p className="py-10 text-center text-sm text-slate-400">All clear. No pending alerts.</p>}
          {items.map((a) => {
            const Icon = ICONS[a.type] || Bell;
            return (
              <div key={a.id} data-testid={`notification-${a.type}`}
                onClick={() => { setOpen(false); nav(a.link); }}
                className="flex cursor-pointer items-start gap-3 border-b border-slate-50 dark:border-slate-800/60 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <span className="mt-0.5 rounded-md bg-amber-100 dark:bg-amber-500/15 p-1.5">
                  <Icon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium leading-tight">{a.title}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{a.body}</span>
                </span>
                <button data-testid={`dismiss-${a.id}`} onClick={(e) => dismiss(e, a.id)}
                  className="text-slate-300 hover:text-slate-500"><X className="h-3.5 w-3.5" /></button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
